import { parseNonNegativeNumber, type QuickSurfaceFormFields } from './quickEstimateGeometry';

/**
 * The four ways a contractor can hand ProWycena a metraż.
 *
 * The copy lives here, next to the domain type it selects, so the wording and the
 * mode it maps to cannot drift apart. `mode` values are the existing identifiers
 * consumed by `calculateSurfaceForQuickForm` — renaming a label must never rename
 * a mode, because the mode is what picks the geometry algorithm.
 */

export type QuickSurfaceMode = QuickSurfaceFormFields['surfaceMode'];

export interface QuickSurfaceModeOption {
  mode: QuickSurfaceMode;
  /** Button caption — what the contractor has, not what the app does. */
  label: string;
  /** One line telling the contractor which numbers this mode will ask for. */
  description: string;
}

export const QUICK_SURFACE_MODE_QUESTION = 'Jak chcesz podać metraż?';

export const QUICK_SURFACE_MODE_OPTIONS: readonly QuickSurfaceModeOption[] = [
  {
    mode: 'ready',
    label: 'Mam gotowy metraż',
    description: 'Masz już policzoną łączną powierzchnię roboczą w m².',
  },
  {
    mode: 'room',
    label: 'Jeden pokój',
    description: 'Długość, szerokość i wysokość — ściany z obwodu oraz opcjonalny sufit.',
  },
  {
    mode: 'apartment',
    // "(szacunek)" in the name, because this mode never measures walls — it
    // derives them from floor area, height and layout.
    label: 'Całe mieszkanie (szacunek)',
    description: 'Szacunek ścian i sufitów na podstawie powierzchni podłogi, wysokości i układu.',
  },
  {
    mode: 'wall',
    label: 'Jedna ściana',
    description: 'Szerokość ściany × wysokość.',
  },
];

/** Field captions that must match the mode the contractor just picked. */
export const QUICK_READY_AREA_FIELD_LABEL = 'Gotowy metraż roboczy (m²)';
export const QUICK_WALL_WIDTH_FIELD_LABEL = 'Szerokość ściany (m)';
export const QUICK_ROOM_LENGTH_FIELD_LABEL = 'Długość pomieszczenia (m)';

/** The caption for the first dimension field, which depends on the mode. */
export function getQuickLengthFieldLabel(mode: QuickSurfaceMode): string {
  return mode === 'wall' ? QUICK_WALL_WIDTH_FIELD_LABEL : QUICK_ROOM_LENGTH_FIELD_LABEL;
}

/**
 * Ceiling toggles say what they add, and how much of it. One room adds one
 * ceiling; the apartment mode adds every ceiling in the flat, which is a much
 * bigger number than "Sufit w zakresie" suggested.
 */
export const QUICK_ROOM_CEILING_LABEL = 'Dolicz sufit';
export const QUICK_APARTMENT_CEILING_LABEL = 'Dolicz sufity w całym mieszkaniu';

/** Only meaningful when a bathroom area was actually entered. */
export const QUICK_BATHROOM_CEILING_ONLY_LABEL = 'Łazienka osobno — dolicz tylko jej sufit';
export const QUICK_BATHROOM_CEILING_ONLY_HINT =
  'Zaznacz, jeśli ściany łazienki wyceniasz osobno, np. pod płytki.';

/** Said plainly under the apartment form: this is an estimate, confirm it. */
export const QUICK_APARTMENT_ESTIMATE_NOTE =
  'To szacunek na podstawie powierzchni podłogi, wysokości i układu. ' +
  'Przed wysłaniem oferty potwierdź metraż.';

/**
 * The bathroom toggle is offered only once there is a bathroom area to apply it
 * to. An empty field or 0 m² means the option changes nothing — the surface
 * calculation already ignores it — so showing it would promise an effect it
 * cannot have.
 */
export function shouldShowBathroomCeilingOnly(bathroomArea: string): boolean {
  return (parseNonNegativeNumber(bathroomArea) ?? 0) > 0;
}
